import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { Loader2 } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { useTranslation } from '@/hooks/useTranslation'

/**
 * AuthCallback handles OAuth and magic link redirects for the main domain.
 *
 * With detectSessionInUrl: true, Supabase automatically:
 * 1. Detects tokens in URL hash (#access_token=...)
 * 2. Exchanges them for a session
 * 3. Fires SIGNED_IN event via onAuthStateChange (handled by AuthContext)
 * 4. Clears the hash from URL
 *
 * This component waits for AuthContext to receive the session via onAuthStateChange,
 * then navigates to the appropriate destination.
 */
const AuthCallback = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const { session, loading, isInitialized } = useAuth()
	const { toast } = useToast()
	const toastShownRef = useRef(false)
	const [hasNavigated, setHasNavigated] = useState(false)
	const [recheckTrigger, setRecheckTrigger] = useState(0)
	const mountTimeRef = useRef(Date.now())
	const initialHashRef = useRef((window as any).__INITIAL_OAUTH_HASH__ || window.location.hash)
	const hasHandledTokenHashRef = useRef(false)
	const MAX_WAIT_TIME = 10000
	const [earlySessionCheck, setEarlySessionCheck] = useState(false)

	useEffect(() => {
		const performEarlySessionCheck = async () => {
			if (earlySessionCheck) return
			
			console.log('[AuthCallback] 🚀 Performing early session check...')
			setEarlySessionCheck(true)
			
			try {
				const { data: { session: directSession }, error } = await supabase.auth.getSession()
				
				if (directSession) {
					console.log('[AuthCallback] ✅ Early session check: Session found!', {
						userId: directSession.user?.id,
						email: directSession.user?.email
					})
				} else if (error) {
					console.error('[AuthCallback] ❌ Early session check error:', error)
				} else {
					console.log('[AuthCallback] ℹ️ Early session check: No session yet')
				}
			} catch (err) {
				console.error('[AuthCallback] ❌ Early session check exception:', err)
			}
		}

		performEarlySessionCheck()
	}, [earlySessionCheck])

	useEffect(() => {
		console.log('[AuthCallback] useEffect triggered', {
			hasNavigated,
			recheckTrigger,
			isInitialized,
			loading,
			hasSession: !!session
		})

		if (hasNavigated) {
			console.log('[AuthCallback] Already navigated, skipping')
			return
		}

		console.log('[AuthCallback] Initial Hash:', initialHashRef.current)
		console.log('[AuthCallback] Current Hash:', window.location.hash)
		console.log('[AuthCallback] Query params:', window.location.search || 'none')
		console.log('[AuthCallback] Auth state:', {
			isInitialized,
			loading,
			hasSession: !!session,
			userId: session?.user?.id
		})

		const attemptManualSessionRecovery = async () => {
			if (
				initialHashRef.current &&
				initialHashRef.current.includes('access_token')
			) {
				console.log('[AuthCallback] Attempting manual session recovery...')

				// Parse the hash to extract tokens
				const hashParams = new URLSearchParams(
					initialHashRef.current.substring(1)
				)
				const accessToken = hashParams.get('access_token')
				const refreshToken = hashParams.get('refresh_token')

				if (accessToken && refreshToken) {
					try {
						// Manually set the session
						const { data, error } = await supabase.auth.setSession({
							access_token: accessToken,
							refresh_token: refreshToken,
						})

						if (data?.session) {
							console.log('[AuthCallback] Manual session recovery successful!')
							return true
						} else if (error) {
							console.error(
								'[AuthCallback] Manual session recovery failed:',
								error
							)
						}
					} catch (e) {
						console.error('[AuthCallback] Manual session recovery error:', e)
					}
				}
			}
			return false
		}

		const tokenHash = searchParams.get('token_hash')
		const otpType = searchParams.get('type')
		const nextPath = searchParams.get('next')

		if (tokenHash && otpType === 'magiclink' && !hasHandledTokenHashRef.current) {
			hasHandledTokenHashRef.current = true
			;(async () => {
				try {
					const { error } = await supabase.auth.verifyOtp({
						type: 'magiclink',
						token_hash: tokenHash,
					})
					if (error) throw error

					setHasNavigated(true)
					navigate(nextPath || '/dashboard', { replace: true })
				} catch (error: any) {
					console.error('[AuthCallback] Magic link token verification failed:', error)
					toast({
						variant: 'destructive',
						title: t('authCallback.authenticationFailed'),
						description: error?.message || t('authCallback.unableToCompleteSignIn'),
					})
					setHasNavigated(true)
					setTimeout(() => navigate('/auth', { replace: true }), 3000)
				}
			})()
			return
		}

		// Check for OAuth errors in URL params
		const errorParam = searchParams.get('error')
		const errorDescription = searchParams.get('error_description')
		const errorCode = searchParams.get('error_code')

		if (errorParam) {
			console.error('[AuthCallback] OAuth error:', {
				errorParam,
				errorCode,
				errorDescription,
			})

			let userMessage =
				errorDescription || t('authCallback.failedToAuthenticate')
			if (errorCode === 'bad_oauth_callback') {
				userMessage = t('authCallback.oauthStateMismatch')
			}

			toast({
				variant: 'destructive',
				title: t('authCallback.authenticationError'),
				description: userMessage,
			})

			setHasNavigated(true)
			setTimeout(() => navigate('/auth', { replace: true }), 3000)
			return
		}

		// CRITICAL CHECK: Detect if hash was cleared without session
		// This indicates Supabase rejected the OAuth callback (likely redirect URL not whitelisted)
		const currentHash = window.location.hash
		const hadTokensInitially =
			initialHashRef.current && initialHashRef.current.includes('access_token')
		const hashNowEmpty = currentHash === '' || currentHash === '#'

		if (
			hadTokensInitially &&
			hashNowEmpty &&
			!session &&
			isInitialized &&
			!loading
		) {
			const elapsedTime = Date.now() - mountTimeRef.current

			if (elapsedTime > 2000) {
				attemptManualSessionRecovery().then((recovered) => {
					if (recovered) {
						// Force a page reload to re-initialize with the new session
						window.location.reload()
					} else {
						console.error('[AuthCallback] CRITICAL CONFIGURATION ERROR')
						console.error(
							'[AuthCallback] OAuth tokens were received but Supabase rejected them'
						)
						console.error(
							'[AuthCallback] This happens when redirect URL is not whitelisted'
						)
						console.error('')
						console.error('[AuthCallback] IMMEDIATE ACTION REQUIRED:')
						console.error(
							'[AuthCallback] 1. Go to https://supabase.com/dashboard'
						)
						console.error('[AuthCallback] 2. Select your project')
						console.error(
							'[AuthCallback] 3. Go to Authentication → URL Configuration'
						)
						console.error(
							'[AuthCallback] 4. Add this EXACT URL to Redirect URLs:'
						)
						console.error(
							`[AuthCallback]    ${window.location.origin}/auth/callback`
						)
						console.error(
							'[AuthCallback] 5. Save changes and try logging in again'
						)
						console.error('')
						console.error('[AuthCallback] Common mistakes:')
						console.error('[AuthCallback] - Wrong protocol (http vs https)')
						console.error('[AuthCallback] - Wrong port (8080 vs 5173)')
						console.error('[AuthCallback] - Trailing slashes')
						console.error('[AuthCallback] - Typos in "callback"')

						toast({
							variant: 'destructive',
							title: t('authCallback.configurationError'),
							description: (
								<div className="space-y-2">
									<p>{t('authCallback.oauthRedirectUrlNotConfigured')}</p>
									<p className="font-mono text-xs bg-black/10 p-1 rounded">
										{t('authCallback.addUrl', { url: `${window.location.origin}/auth/callback` })}
									</p>
									<p className="text-xs">
										{t('authCallback.checkConsoleForInstructions')}
									</p>
								</div>
							),
							duration: 10000, // Show for longer
						})

						setHasNavigated(true)
						setTimeout(() => navigate('/auth', { replace: true }), 10000) // Give more time to read
					}
				})
				return
			}
		}

		// Wait for AuthContext to initialize
		if (!isInitialized) {
			console.log('[AuthCallback] Waiting for AuthContext to initialize...')
			return
		}

		// Wait for loading to complete
		if (loading) {
			console.log('[AuthCallback] Waiting for auth loading to complete...')
			return
		}

		// Check if we have a session from AuthContext
		// Note: Supabase's onAuthStateChange (in AuthContext) already handled the hash tokens
		if (session?.user) {
			console.log(
				'[AuthCallback] Session established via onAuthStateChange, navigating...'
			)

			// Check for redirectAfterLogin from localStorage (e.g., from invite acceptance flow)
			const redirectAfterLogin = localStorage.getItem('redirectAfterLogin')
			if (redirectAfterLogin) {
				localStorage.removeItem('redirectAfterLogin')
				console.log('[AuthCallback] Redirecting to saved URL after login:', redirectAfterLogin)
				setHasNavigated(true)
				navigate(redirectAfterLogin, { replace: true })
				return
			}

			// Determine destination based on redirect parameter
			const redirectTo = searchParams.get('redirect')
			const destination =
				redirectTo === 'pricing' ? '/settings?tab=billing' : '/dashboard'

			if (!toastShownRef.current) {
				toast({
					title: t('authCallback.welcome'),
					description: t('authCallback.successfullySignedIn'),
				})
				toastShownRef.current = true
			}

			setHasNavigated(true)
			navigate(destination, { replace: true })
		} else {
			// No session yet - check if we should keep waiting
			const elapsedTime = Date.now() - mountTimeRef.current

			if (elapsedTime < MAX_WAIT_TIME) {
				// Still within grace period - schedule a recheck
				// This allows Supabase time to process hash tokens on slow networks
				console.log(
					`[AuthCallback] No session yet, rechecking in 500ms (elapsed: ${Math.floor(
						elapsedTime / 1000
					)}s / ${MAX_WAIT_TIME / 1000}s)`
				)
				const timer = setTimeout(() => {
					setRecheckTrigger((prev) => prev + 1) // Trigger effect to re-run
				}, 500)
				return () => clearTimeout(timer)
			}

			// Waited long enough - auth failed
			console.error(
				'[AuthCallback] No session after waiting',
				elapsedTime,
				'ms'
			)
			console.error(
				'[AuthCallback] This means Supabase failed to establish session from URL tokens'
			)
			console.error('[AuthCallback] Possible causes:')
			console.error('  1. Redirect URL not whitelisted in Supabase')
			console.error('  2. OAuth state validation failed')
			console.error('  3. Tokens were invalid or expired')
			console.error('  4. Network too slow (max wait time exceeded)')

			toast({
				variant: 'destructive',
				title: t('authCallback.authenticationFailed'),
				description: t('authCallback.unableToCompleteSignIn'),
			})

			setHasNavigated(true)
			setTimeout(() => navigate('/auth', { replace: true }), 3000)
		}
	}, [
		session,
		loading,
		isInitialized,
		searchParams,
		navigate,
		toast,
		hasNavigated,
		recheckTrigger,
	])

	return (
		<div className="flex h-screen w-full items-center justify-center bg-gradient-to-b from-[#ffffff] to-[#866bfe2a]">
			<div className="text-center">
				<Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
				<p className="text-lg font-medium text-gray-900 dark:text-gray-100">
					{t('authCallback.completingSignIn')}
				</p>
				<p className="text-sm text-muted-foreground mt-2">
					{t('authCallback.pleaseWaitWhileAuthenticating')}
				</p>
			</div>
		</div>
	)
}

export default AuthCallback
